/**
 * API Response Validation
 *
 * Provides runtime validation for API responses using Zod schemas.
 * This eliminates type assertions and ensures type safety at runtime.
 */
import { z } from 'zod';
/**
 * ValidationError
 *
 * Custom error for validation failures
 */
export class ValidationError extends Error {
    details;
    path;
    constructor(message, details = {}, path = []) {
        super(message);
        this.details = details;
        this.path = path;
        this.name = 'ValidationError';
    }
}
/**
 * Validate API response against Zod schema
 *
 * @param schema - Zod schema to validate against
 * @param data - Raw data from API response
 * @param options - Validation options
 * @returns Validated and typed data
 * @throws ValidationError if validation fails
 */
export function validateApiResponse(schema, data, options = {}) {
    try {
        return schema.parse(data);
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            const details = {};
            const path = options.path || [];
            // Format validation errors for better debugging
            error.issues.forEach((issue) => {
                const fieldPath = [...path, ...issue.path].join('.');
                details[fieldPath] = issue.message;
            });
            const message = options.errorMessage || `API response validation failed at ${path.join('.')}`;
            throw new ValidationError(message, details, path);
        }
        // Re-throw non-Zod errors
        throw error;
    }
}
/**
 * Validate API response wrapper
 *
 * Validates the entire API response structure including the wrapper
 */
export function validateApiData(dataSchema, data, options = {}) {
    // Define the API response wrapper schema
    const apiResponseSchema = z.object({
        success: z.boolean(),
        data: dataSchema.optional(),
        message: z.string().optional(),
        error: z
            .object({
            code: z.string(),
            message: z.string(),
            details: z.record(z.string(), z.unknown()).optional(),
        })
            .optional(),
        meta: z
            .object({
            timestamp: z.string().datetime(),
            requestId: z.string().optional(),
        })
            .optional(),
    });
    const validated = validateApiResponse(apiResponseSchema, data, {
        path: options.path,
        errorMessage: options.errorMessage || 'Invalid API response structure',
    });
    if (validated.data === undefined) {
        throw new ValidationError('API response missing data field', {}, options.path);
    }
    return validated.data;
}
/**
 * Safe type guard for API responses
 *
 * Returns a Result type instead of throwing
 */
export function safeValidateApiResponse(schema, data, options = {}) {
    try {
        const validatedData = validateApiResponse(schema, data, options);
        return { success: true, data: validatedData };
    }
    catch (error) {
        if (error instanceof ValidationError) {
            return { success: false, error };
        }
        // Convert unexpected errors to ValidationError
        const validationError = new ValidationError(options.errorMessage || 'Unexpected validation error', {}, options.path);
        return { success: false, error: validationError };
    }
}
/**
 * Batch validation for multiple API responses
 */
export function validateBatchResponses(validations) {
    const results = {};
    const errors = {};
    for (const validation of validations) {
        try {
            results[validation.key] = validateApiResponse(validation.schema, validation.data, {
                path: [validation.key],
            });
        }
        catch (error) {
            if (error instanceof ValidationError) {
                errors[validation.key] = error;
            }
            else {
                errors[validation.key] = new ValidationError(`Unexpected error validating ${validation.key}`, {}, [validation.key]);
            }
        }
    }
    if (Object.keys(errors).length > 0) {
        const errorMessage = `Batch validation failed for: ${Object.keys(errors).join(', ')}`;
        const allDetails = Object.entries(errors).reduce((acc, [key, error]) => ({
            ...acc,
            ...Object.fromEntries(Object.entries(error.details).map(([k, v]) => [`${key}.${k}`, v])),
        }), {});
        throw new ValidationError(errorMessage, allDetails);
    }
    return results;
}
/**
 * Create a validator for specific API endpoints
 */
export function createEndpointValidator(schema) {
    return {
        validate: (data, options) => validateApiResponse(schema, data, options),
        safeValidate: (data, options) => safeValidateApiResponse(schema, data, options),
        validateWrapper: (data, options) => validateApiData(schema, data, options),
    };
}
//# sourceMappingURL=validation.js.map